import { useState } from "react";
import { useCommunityBroadcasts } from "@/hooks/useCommunityBroadcasts";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, MapPin, Clock, CreditCard, Users, ThumbsUp, ThumbsDown, Send, ChevronDown, ChevronUp, Phone } from "lucide-react";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";

export default function Community() {
  const { user } = useAuth();
  const {
    broadcasts, loading, toggleInterest, referToAgency,
    getResponsesForBroadcast, getMyResponse, closeBroadcast,
  } = useCommunityBroadcasts();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [referringId, setReferringId] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const myBroadcasts = broadcasts.filter(b => b.broadcaster_user_id === user?.id);
  const communityBroadcasts = broadcasts.filter(b => b.broadcaster_user_id !== user?.id && b.status === "active");

  async function handleInterest(broadcastId: string, interested: boolean) {
    try {
      await toggleInterest(broadcastId, interested);
      toast.success(interested ? "Marked as interested!" : "Marked as not interested");
    } catch {
      toast.error("Failed to update interest");
    }
  }

  async function handleRefer(broadcastId: string, receivingUserId: string) {
    setReferringId(broadcastId);
    try {
      await referToAgency(broadcastId, receivingUserId);
      toast.success("Referral sent! The client has been notified.");
    } catch {
      toast.error("Failed to process referral");
    } finally {
      setReferringId(null);
    }
  }

  async function handleClose(broadcastId: string) {
    try {
      await closeBroadcast(broadcastId);
      toast.success("Broadcast closed");
    } catch {
      toast.error("Failed to close broadcast");
    }
  }

  return (
    <TooltipProvider>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Community Board</h1>
          <p className="text-muted-foreground">Share leads you can't serve and discover referral opportunities from other agencies.</p>
        </div>

        {/* My Broadcasts */}
        {myBroadcasts.length > 0 && (
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-foreground">My Broadcasts</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {myBroadcasts.map(b => {
                const interestedAgencies = getResponsesForBroadcast(b.id);
                const isExpanded = expandedId === b.id;
                return (
                  <Card key={b.id} className="relative">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">
                          <span className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            {b.zip_code || "No ZIP"}
                          </span>
                        </CardTitle>
                        <Badge variant={b.status === "active" ? "default" : b.status === "referred" ? "secondary" : "outline"}>
                          {b.status === "referred" ? "Referred" : b.status === "active" ? "Active" : "Closed"}
                        </Badge>
                      </div>
                      <CardDescription className="line-clamp-2">{b.care_needs || "No details"}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        {b.payer_source && <span className="flex items-center gap-1"><CreditCard className="h-3.5 w-3.5" />{b.payer_source}</span>}
                        {b.hours_needed && <span>{b.hours_needed} hrs/wk</span>}
                        {b.urgency && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{b.urgency}/5</span>}
                      </div>

                      <div className="flex items-center justify-between">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpandedId(isExpanded ? null : b.id)}
                          className="gap-1 text-sm"
                        >
                          <Users className="h-4 w-4" />
                          {interestedAgencies.length} interested
                          {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        </Button>
                        {b.status === "active" && (
                          <Button variant="outline" size="sm" onClick={() => handleClose(b.id)}>Close</Button>
                        )}
                      </div>

                      {isExpanded && interestedAgencies.length > 0 && (
                        <div className="space-y-2 rounded-lg border p-3">
                          <h4 className="text-xs font-semibold text-muted-foreground uppercase">Interested Agencies</h4>
                          {interestedAgencies.map(r => (
                            <div key={r.id} className="flex items-center justify-between rounded-md bg-muted p-2">
                              <div>
                                <p className="text-sm font-medium text-foreground">{r.responder_agency_name}</p>
                                {r.responder_phone && (
                                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Phone className="h-3 w-3" />{r.responder_phone}
                                  </p>
                                )}
                              </div>
                              {b.status === "active" && (
                                <Button
                                  size="sm"
                                  onClick={() => handleRefer(b.id, r.responder_user_id)}
                                  disabled={referringId === b.id}
                                  className="gap-1"
                                >
                                  {referringId === b.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Send className="h-3 w-3" />
                                  )}
                                  Refer to This Agency
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {isExpanded && interestedAgencies.length === 0 && (
                        <p className="text-sm text-muted-foreground italic">No agencies have expressed interest yet.</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>
        )}

        {/* Community Feed */}
        <section className="space-y-4">
          <h2 className="text-lg font-semibold text-foreground">Community Feed</h2>
          {communityBroadcasts.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center text-muted-foreground">
                <Users className="mx-auto mb-3 h-10 w-10 opacity-50" />
                <p>No active broadcasts in the community right now.</p>
                <p className="text-sm mt-1">When agencies share leads they can't serve, they'll appear here.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {communityBroadcasts.map(b => {
                const myResponse = getMyResponse(b.id);
                const isInterested = myResponse?.interested === true;
                return (
                  <Card key={b.id}>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        {b.zip_code || "No ZIP"}
                      </CardTitle>
                      <CardDescription>
                        <span className="text-xs text-muted-foreground">
                          Posted by {b.broadcaster_agency_name} · {new Date(b.created_at).toLocaleDateString()}
                        </span>
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <p className="text-sm text-foreground">{b.care_needs || "No care needs specified"}</p>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                        {b.payer_source && (
                          <span className="flex items-center gap-1"><CreditCard className="h-3.5 w-3.5" />{b.payer_source}</span>
                        )}
                        {b.hours_needed && <span>{b.hours_needed} hrs/wk</span>}
                        {b.urgency && (
                          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />Urgency: {b.urgency}/5</span>
                        )}
                      </div>

                      <div className="flex gap-2 pt-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant={isInterested ? "default" : "outline"}
                              size="sm"
                              className="gap-1 flex-1"
                              onClick={() => handleInterest(b.id, true)}
                            >
                              <ThumbsUp className="h-3.5 w-3.5" />
                              Interested
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Express interest in serving this client</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant={myResponse && !myResponse.interested ? "secondary" : "ghost"}
                              size="sm"
                              className="gap-1"
                              onClick={() => handleInterest(b.id, false)}
                            >
                              <ThumbsDown className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Not interested</TooltipContent>
                        </Tooltip>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </TooltipProvider>
  );
}
